import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography, Fade, Grow, keyframes, useMediaQuery } from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import io from 'socket.io-client';
import confetti from 'canvas-confetti';
import { isAdminAppPath, isPrizePopupBlockedPath } from '../../utils/publicAccessPaths';
import { mediaUrl, prizeValueLabel, prizeVisibleOnPath } from '../AdminSide/prizes/prizePaletteShared';
import LiveWheelDisplay from './LiveWheelDisplay';
import { entryFromSpin, prizeIndexFromSpin, sliceSignature } from './DefaultStyleWheel';
import usePrizeWheelSpinAudio from '../../hooks/usePrizeWheelSpinAudio';
import usePrizePaletteEventAudio from '../../hooks/usePrizePaletteEventAudio';

const StableLiveWheel = React.memo(LiveWheelDisplay);

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const readSessionId = () => {
  try { return sessionStorage.getItem('active_user_session_id') || ''; }
  catch { return ''; }
};

const readDeviceId = () => {
  try {
    let id = localStorage.getItem('device_id');
    if (!id) { id = `dev_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`; localStorage.setItem('device_id', id); }
    return id;
  } catch { return ''; }
};

const readUserId = () => {
  try {
    const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (raw) { const u = JSON.parse(raw); return u?.id ?? u?.user_id ?? null; }
  } catch { /* ignore */ }
  return null;
};

// Inline mode is the admin live desk: same cards as players, kept in the page
// flow so the host bars stay clickable. Page mode is the full-screen audience popup.
const PrizeFrameContext = React.createContext({
  inline: false,
  onHide: null,
  hideBusy: false,
});

/* ── keyframes ────────────────────────────────────────────────── */

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 12px 0 var(--glow-color); }
  50%      { box-shadow: 0 0 42px 10px var(--glow-color); }
`;

const floatUp = keyframes`
  0%   { transform: translateY(32px) scale(0.92); opacity: 0; }
  100% { transform: translateY(0) scale(1); opacity: 1; }
`;

const popIn = keyframes`
  0%   { transform: scale(0.5) rotate(-6deg); opacity: 0; }
  60%  { transform: scale(1.06) rotate(1deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50%      { opacity: 1; }
`;

// User-side stage: fill the screen with a margin on every edge.
const stageMaxW = 'calc(100vw - clamp(32px, 6vmin, 80px) - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px))';
const stageMaxH = 'calc(100dvh - clamp(32px, 6vmin, 80px) - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))';
const stageSquare = `min(${stageMaxW}, ${stageMaxH})`;
// Collage and wheel popups: extra side gap so a phone does not sit on the screen edge.
const showPopupMaxW = 'calc(100vw - clamp(48px, 12vw, 96px) - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px))';
const showPopupSquare = `min(${showPopupMaxW}, ${stageMaxH})`;

const userStageFrameSx = {
  width: stageMaxW,
  height: stageMaxH,
  maxWidth: stageMaxW,
  maxHeight: stageMaxH,
  overflow: 'hidden',
  boxSizing: 'border-box',
  flexShrink: 0,
};

// Prize show and award: same on-screen size, as a 1:1 box. The image stays 3:1.
const prizeStageFrameSx = {
  width: stageSquare,
  height: stageSquare,
  maxWidth: stageMaxW,
  maxHeight: stageMaxH,
  aspectRatio: '1 / 1',
  overflow: 'hidden',
  boxSizing: 'border-box',
  flexShrink: 0,
};

const useElementBox = (active) => {
  const ref = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useEffect(() => {
    if (!active) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const styles = window.getComputedStyle(el);
      const padX = (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
      const padY = (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
      const w = Math.max(0, Math.floor(el.clientWidth - padX));
      const h = Math.max(0, Math.floor(el.clientHeight - padY));
      setBox((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [active]);
  return [ref, box];
};

const slideUp = keyframes`
  0%   { transform: translateY(20px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
`;

const spin = keyframes`
  0%   { transform: rotateY(0deg) scale(0.6); opacity: 0.3; }
  50%  { transform: rotateY(540deg) scale(1.1); opacity: 1; }
  100% { transform: rotateY(720deg) scale(1); opacity: 1; }
`;

/* ── main overlay ─────────────────────────────────────────────── */

const PrizeRevealOverlay = ({
  variant = 'page',
  forcedScreen = null,
  onHide = null,
  hideBusy = false,
}) => {
  const inline = variant === 'inline';
  const location = useLocation();
  const [reveal, setReveal] = useState(null);
  const [screen, setScreen] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const [spectatorGame, setSpectatorGame] = useState('');
  const [revealing, setRevealing] = useState(false);
  const prevRevealRef = useRef(null);
  const versionRef = useRef(0);
  const hide = !inline && (isAdminAppPath(location.pathname) || isPrizePopupBlockedPath(location.pathname));
  const viewScreen = inline ? forcedScreen : screen;
  const viewReveal = inline ? null : reveal;
  const winnerActive = !inline && viewReveal && (viewReveal.status === 'shown' || viewReveal.status === 'awarded');
  const scope = winnerActive ? (viewReveal.game || viewScreen?.game || 'all') : (viewScreen?.game || 'all');
  const soundScreen = winnerActive ? viewReveal : viewScreen;
  usePrizePaletteEventAudio(inline ? null : soundScreen, {
    audible: !inline && !hide && prizeVisibleOnPath(location.pathname, scope, spectatorGame),
    ready: !inline && hydrated,
  });

  const applyState = useCallback((payload) => {
    if (!payload || typeof payload !== 'object') return;
    if (Object.prototype.hasOwnProperty.call(payload, 'version')) {
      const version = Number(payload.version) || 0;
      // A reconnect can deliver an older snapshot after Hide. Ignore it.
      if (version < versionRef.current) return;
      if (version > versionRef.current) versionRef.current = version;
    } else if (versionRef.current > 0) {
      return;
    }
    const hasScreen = Object.prototype.hasOwnProperty.call(payload, 'screen');
    const hasReveal = Object.prototype.hasOwnProperty.call(payload, 'reveal');
    if (hasReveal) setReveal(payload.reveal || null);
    if (hasScreen) setScreen(payload.screen || null);
    if (hasScreen && !payload.screen) setReveal(null);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (inline || hide) {
      if (!inline) { setReveal(null); setScreen(null); }
      return undefined;
    }
    let socket, stopped = false;

    const pull = async (sessionId) => {
      if (!sessionId) return;
      try {
        const res = await fetch(`${API_BASE}/api/prize-palette/active?session_id=${encodeURIComponent(sessionId)}`, { cache: 'no-store' });
        const data = await res.json();
        if (stopped || !data.success) return;
        applyState(data);
      } catch { /* socket carries the room-wide screen */ }
    };

    socket = io(API_BASE, { withCredentials: true, forceNew: true });
    const join = () => socket.emit('prize:join', { session_id: readSessionId() });
    socket.on('connect', join);
    socket.on('prize:state', applyState);
    socket.on('prize:screen', applyState);

    const waitForSession = setInterval(() => {
      const next = readSessionId();
      if (next && socket.connected && socket._joinedSession !== next) {
        socket._joinedSession = next;
        join();
        pull(next);
      }
    }, 1000);

    const onVisible = () => {
      if (document.visibilityState === 'visible') { join(); pull(readSessionId()); }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      stopped = true;
      clearInterval(waitForSession);
      document.removeEventListener('visibilitychange', onVisible);
      socket.disconnect();
    };
  }, [inline, hide, applyState]);

  // Spectate drops live sockets often enough to miss Hide. Re-read the
  // public screen and apply it even if another socket event arrived first.
  useEffect(() => {
    const path = String(location.pathname || '').split('?')[0].toLowerCase();
    if (path !== '/spectate') return undefined;
    let stopped = false;
    const pull = async () => {
      const sessionId = readSessionId() || 'spectator';
      try {
        const res = await fetch(`${API_BASE}/api/prize-palette/active?session_id=${encodeURIComponent(sessionId)}`, { cache: 'no-store' });
        const data = await res.json();
        if (stopped || !data.success) return;
        applyState(data);
      } catch { /* keep the last applied snapshot */ }
    };
    pull();
    const timer = setInterval(pull, 1000);
    return () => { stopped = true; clearInterval(timer); };
  }, [location.pathname, applyState]);

  useEffect(() => {
    const path = String(location.pathname || '').split('?')[0].toLowerCase();
    if (path !== '/spectate') { setSpectatorGame(''); return undefined; }
    let stopped = false;
    const pull = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/spectator/live`);
        const data = await res.json();
        if (!stopped) setSpectatorGame(data?.status === 'live' ? data.game_type || '' : '');
      } catch { if (!stopped) setSpectatorGame(''); }
    };
    pull();
    const timer = setInterval(pull, 4000);
    return () => { stopped = true; clearInterval(timer); };
  }, [location.pathname]);

  useEffect(() => {
    const key = viewReveal
      ? `reveal-${viewReveal.id}`
      : (viewScreen?.kind === 'display' || viewScreen?.kind === 'collage' || (inline && viewScreen?.kind === 'reveal'))
        ? `${viewScreen.kind}-${viewScreen.prize_id || viewScreen.award_id || viewScreen.token}`
        : null;
    const isNew = key && prevRevealRef.current !== key;
    prevRevealRef.current = key;
    if (isNew) {
      setRevealing(true);
      const timer = setTimeout(() => setRevealing(false), 1600);
      return () => clearTimeout(timer);
    }
  }, [inline, viewReveal, viewScreen]);

  if (hide) return null;

  const sessionId = readSessionId();
  const audienceReveal = !winnerActive && viewScreen?.kind === 'reveal' && viewScreen.session_id !== sessionId;
  const prizeDisplay = !winnerActive && viewScreen?.kind === 'display' && viewScreen?.prize_type !== 'wheel';
  const collageDisplay = !winnerActive && (viewScreen?.kind === 'collage' || (viewScreen?.kind === 'display' && viewScreen?.prize_type === 'wheel'));
  const isSpinWheelPage = location.pathname.toLowerCase().split('?')[0] === '/spinningwheel';
  const wheelDisplay = !winnerActive && viewScreen?.kind === 'wheel' && !isSpinWheelPage;
  if (!inline && !prizeVisibleOnPath(location.pathname, scope, spectatorGame)) return null;

  let body = null;
  if (winnerActive) body = <WinnerCard reveal={viewReveal} revealing={revealing} />;
  else if (inline && viewScreen?.kind === 'reveal') {
    body = (
      <WinnerCard
        reveal={{ ...viewScreen, player_name: viewScreen.winner_name }}
        revealing={revealing}
        host
      />
    );
  }
  else if (wheelDisplay) body = <WheelCard screen={viewScreen} />;
  else if (collageDisplay) body = <CollageCard screen={viewScreen} revealing={revealing} />;
  else if (prizeDisplay) body = <DisplayCard screen={viewScreen} revealing={revealing} />;
  else if (audienceReveal) body = <AudienceCard screen={viewScreen} />;
  if (!body) return null;

  return (
    <PrizeFrameContext.Provider value={{ inline, onHide: inline ? onHide : null, hideBusy }}>
      {body}
    </PrizeFrameContext.Provider>
  );
};

/* ── backdrop ─────────────────────────────────────────────────── */

const Backdrop = ({ children, strong, tint = 'rgba(100,72,10,0.55)' }) => {
  const { inline, onHide, hideBusy } = useContext(PrizeFrameContext);
  const stageBackground = strong
    ? `radial-gradient(ellipse at 50% 38%, ${tint} 0%, rgba(0,0,0,0.94) 70%)`
    : 'rgba(0,0,0,0.72)';
  const hideButton = inline && onHide ? (
    <Button
      size="small"
      variant="contained"
      startIcon={<VisibilityOffIcon sx={{ fontSize: 16 }} />}
      disabled={hideBusy}
      onClick={(event) => {
        event.stopPropagation();
        onHide();
      }}
      sx={{
        alignSelf: 'flex-end',
        bgcolor: '#fff',
        color: '#111',
        fontWeight: 800,
        borderRadius: 2,
        minWidth: 84,
        px: 1.25,
        py: 0.45,
        fontSize: 12,
        boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
        '&.Mui-disabled': { bgcolor: '#eee', color: '#888' },
        '&:hover': { bgcolor: '#f3f3f3' },
      }}
    >
      Hide
    </Button>
  ) : null;

  if (inline) {
    return (
      <Fade in timeout={400}>
        <Box sx={{
          width: '100%',
          mb: 2,
          maxHeight: 'min(70vh, 560px)',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1.25,
          p: { xs: 1.5, sm: 2 },
          boxSizing: 'border-box',
          borderRadius: 3,
          background: stageBackground,
          border: '1px solid rgba(255,255,255,0.08)',
        }}>
          {hideButton}
          <Box sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& > *': { maxWidth: '100%' },
          }}>
            {children}
          </Box>
        </Box>
      </Fade>
    );
  }

  return (
    <Fade in timeout={400}>
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 2000,
        width: '100vw',
        height: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        boxSizing: 'border-box',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        background: stageBackground,
      }}>
        {children}
      </Box>
    </Fade>
  );
};

/* ── gift box fallback (no prize image) ───────────────────────── */

const GiftBoxArt = ({ color, value, size, fluid = false }) => {
  const ribbon = color || '#FFD54F';
  const lid = '#c0392b';
  const body = '#e74c3c';
  return (
    <Box sx={{
      width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: `radial-gradient(circle at 50% 40%, ${ribbon}22 0%, transparent 70%)`,
      position: 'relative',
    }}>
      <Box
        component="svg"
        viewBox="0 0 120 120"
        sx={{
          width: fluid ? '42%' : size * 0.55,
          height: fluid ? 'auto' : size * 0.55,
          aspectRatio: '1 / 1',
        }}
        aria-hidden
      >
        {/* lid */}
        <rect x="18" y="28" width="84" height="22" rx="4" fill={lid} />
        {/* body */}
        <rect x="24" y="48" width="72" height="52" rx="4" fill={body} />
        {/* vertical ribbon */}
        <rect x="52" y="28" width="16" height="72" fill={ribbon} />
        {/* horizontal ribbon */}
        <rect x="18" y="42" width="84" height="12" fill={ribbon} />
        {/* bow left */}
        <ellipse cx="44" cy="24" rx="14" ry="10" fill={ribbon} opacity="0.95" />
        {/* bow right */}
        <ellipse cx="76" cy="24" rx="14" ry="10" fill={ribbon} opacity="0.95" />
        {/* bow knot */}
        <circle cx="60" cy="26" r="7" fill={ribbon} />
      </Box>
      {value ? (
        <Typography sx={{
          mt: 0.5, color: ribbon, fontWeight: 900,
          fontSize: fluid ? 'clamp(18px, 3vw, 36px)' : (size > 140 ? 22 : 15),
          letterSpacing: 0.5, textShadow: '0 2px 8px rgba(0,0,0,0.5)',
        }}>
          {value}
        </Typography>
      ) : null}
    </Box>
  );
};

/* ── prize image (600×200 / 3:1) ─────────────────────────────── */

const PrizeArt = ({ image, name, color, size = 300, glowing, value, fluid = false }) => (
  <Box sx={{
    '--glow-color': `${color}88`,
    width: '100%',
    maxWidth: fluid ? '100%' : size,
    aspectRatio: '3 / 1',
    mx: 'auto',
    borderRadius: fluid ? 4 : 3,
    overflow: 'hidden',
    bgcolor: '#141414',
    border: `3px solid ${color}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: glowing ? `${glow} 2s ease-in-out infinite` : 'none',
    boxShadow: `0 18px 50px ${color}33`,
  }}>
    {image
      ? <Box component="img" src={image} alt={name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      : <GiftBoxArt color={color} value={value} size={110} fluid={fluid} />}
  </Box>
);

const StageHeader = ({ color, kicker, title, large = false, compact = false }) => (
  <Box sx={{
    width: '100%',
    flexShrink: 0,
    position: 'relative',
    zIndex: 2,
    py: compact ? 1 : (large ? 1.8 : 1.4),
    px: compact ? 2 : (large ? 3 : 2.5),
    background: `linear-gradient(135deg, ${color}, ${color}bb)`,
    color: '#111',
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent)',
      backgroundSize: '200% 100%',
      animation: `${shimmer} 2.8s linear infinite`,
    },
  }}>
    {kicker ? (
      <Typography sx={{ fontWeight: 800, letterSpacing: 1.6, textTransform: 'uppercase', fontSize: compact ? 10 : (large ? 'clamp(11px, 1.4vw, 16px)' : 10), opacity: 0.72, position: 'relative', zIndex: 1 }}>
        {kicker}
      </Typography>
    ) : null}
    <Typography sx={{ fontWeight: 900, letterSpacing: 0.4, fontSize: compact ? 'clamp(16px, 2.4vw, 28px)' : (large ? 'clamp(20px, 2.8vw, 36px)' : { xs: 16, sm: 18 }), lineHeight: 1.2, position: 'relative', zIndex: 1 }}>
      {title}
    </Typography>
  </Box>
);

const PrizeStageBody = ({ color, image, name, value, glowing, revealing, lead = null, children }) => {
  const [regionRef, region] = useElementBox(true);
  const textRef = useRef(null);
  const leadRef = useRef(null);
  const [textH, setTextH] = useState(0);
  const [leadH, setLeadH] = useState(0);

  useEffect(() => {
    const watch = (el, setHeight) => {
      if (!el) return undefined;
      const measure = () => {
        const next = Math.ceil(el.getBoundingClientRect().height);
        setHeight((prev) => (prev === next ? prev : next));
      };
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      return () => observer.disconnect();
    };
    const stopText = watch(textRef.current, setTextH);
    const stopLead = watch(leadRef.current, setLeadH);
    return () => {
      if (stopText) stopText();
      if (stopLead) stopLead();
    };
  }, [lead]);

  const availW = Math.max(0, region.w);
  const availH = Math.max(0, region.h - textH - leadH - 16);
  const artW = availW > 0 && availH > 0 ? Math.min(availW, availH * 3) : 0;

  return (
    <Box
      ref={regionRef}
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        px: { xs: 2, sm: 3.5 },
        py: 2,
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{
        width: '100%',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 0,
      }}>
        {lead ? (
          <Box ref={leadRef} sx={{ width: '100%', flexShrink: 0, pb: 2, textAlign: 'center' }}>
            {lead}
          </Box>
        ) : null}
        <Box sx={{
          width: artW > 0 ? `${artW}px` : '100%',
          maxWidth: '100%',
          flexShrink: 1,
          minHeight: 0,
          animation: revealing ? `${spin} 1.5s cubic-bezier(.3,.9,.4,1)` : `${floatUp} 0.6s ease-out`,
          perspective: 600,
        }}>
          <PrizeArt image={image} name={name} color={color} glowing={glowing} value={value} fluid />
        </Box>
        <Box ref={textRef} sx={{ width: '100%', flexShrink: 0, pt: 2, textAlign: 'center' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

/* ── winner card ──────────────────────────────────────────────── */

const WinnerCard = ({ reveal, revealing, host = false }) => {
  const { inline } = useContext(PrizeFrameContext);
  const stage = !inline;
  const awarded = reveal.status === 'awarded';
  const color = reveal.category_color || '#FFD54F';
  const image = mediaUrl(reveal.prize_image_url);
  const value = prizeValueLabel(reveal);
  const name = reveal.prize_name || 'Prize';
  const confettiFired = useRef(false);
  const winnerName = reveal.player_name || reveal.winner_name || 'Player';

  useEffect(() => {
    if (host) return undefined;
    if (awarded && !confettiFired.current) {
      confettiFired.current = true;
      const end = Date.now() + 2400;
      const colors = [color, '#ffffff', '#FFD54F', '#FF8A65', '#81C784'];
      const frame = () => {
        confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0, y: 0.7 }, colors, scalar: 1.1, zIndex: 12000 });
        confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1, y: 0.7 }, colors, scalar: 1.1, zIndex: 12000 });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
    }
    return () => { confettiFired.current = false; };
  }, [awarded, color, host]);

  const note = host
    ? `${winnerName} ${awarded ? 'just won this prize.' : 'is seeing this prize.'}`
    : awarded
      ? `${reveal.player_name || 'You'}, this prize is yours.`
      : 'Hold on. Your host is about to award it.';

  const copy = (
    <>
      <Typography sx={{
        color: '#fff', fontWeight: 900, mt: stage ? 0 : 2.25, lineHeight: 1.05,
        fontSize: stage ? 'clamp(28px, 4.6vw, 64px)' : { xs: 28, sm: 40 },
        animation: stage ? 'none' : `${slideUp} 0.5s ease-out ${revealing ? '1.7s' : '0.35s'} both`,
      }}>
        {name}
      </Typography>
      {image && value ? (
        <Box sx={{
          display: 'inline-flex', mt: stage ? 1.5 : 1.25, px: stage ? 2 : 1.5, py: stage ? 0.55 : 0.4, borderRadius: 999,
          bgcolor: `${color}22`, border: `1px solid ${color}88`,
        }}>
          <Typography sx={{ color, fontWeight: 800, fontSize: stage ? 'clamp(16px, 2.2vw, 28px)' : { xs: 15, sm: 18 } }}>{value}</Typography>
        </Box>
      ) : null}
      <Typography sx={{
        color: 'rgba(255,255,255,0.78)', mt: stage ? 1.5 : 1.75,
        fontSize: stage ? 'clamp(15px, 2vw, 24px)' : { xs: 14, sm: 16 },
        animation: stage ? 'none' : `${slideUp} 0.5s ease-out ${revealing ? '2s' : '0.55s'} both`,
      }}>
        {note}
      </Typography>
    </>
  );

  return (
    <Backdrop strong tint={`${color}66`}>
      <Box sx={{
        position: 'relative',
        ...(stage ? prizeStageFrameSx : {
          width: 'min(560px, 96vw)',
          maxHeight: '92dvh',
        }),
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4,
        bgcolor: '#100e08',
        border: `2px solid ${color}`,
        textAlign: 'center',
        boxShadow: `0 0 80px ${color}33, 0 24px 70px rgba(0,0,0,0.65)`,
        animation: `${popIn} 700ms cubic-bezier(.2,1.4,.4,1)`,
      }}>
        <StageHeader
          large={stage}
          color={color}
          kicker={reveal.category_name || 'Prize'}
          title={host ? (awarded ? 'Awarded' : 'On screen') : (awarded ? 'You won' : 'This one is yours')}
        />
        {stage ? (
          <PrizeStageBody color={color} image={image} name={name} value={value} glowing revealing={revealing}>
            {copy}
          </PrizeStageBody>
        ) : (
          <Box sx={{ px: { xs: 2, sm: 4 }, pt: 2.5, pb: 3.5 }}>
            <Box sx={{
              animation: revealing ? `${spin} 1.5s cubic-bezier(.3,.9,.4,1)` : `${floatUp} 0.6s ease-out`,
              perspective: 600,
            }}>
              <PrizeArt image={image} name={name} color={color} size={520} glowing value={value} />
            </Box>
            {copy}
          </Box>
        )}
      </Box>
    </Backdrop>
  );
};

/* ── collage (600×600 / 1:1, show/hide only) ─────────────────── */

const CollageCard = ({ screen, revealing }) => {
  const { inline } = useContext(PrizeFrameContext);
  const color = screen.category_color || '#90CAF9';
  const image = mediaUrl(screen.prize_image_url);
  const name = screen.prize_name || 'Collage';

  return (
    <Backdrop strong tint={`${color}55`}>
      <Box sx={{
        position: 'relative',
        width: inline ? 'min(420px, 100%)' : showPopupSquare,
        height: inline ? 'auto' : undefined,
        maxWidth: inline ? '100%' : showPopupMaxW,
        maxHeight: stageMaxH,
        aspectRatio: '1 / 1',
        boxSizing: 'border-box',
        borderRadius: 4,
        overflow: 'hidden',
        bgcolor: '#100e08',
        border: `2px solid ${color}`,
        boxShadow: `0 0 80px ${color}44, 0 24px 60px rgba(0,0,0,0.55)`,
        animation: `${popIn} 700ms cubic-bezier(.2,1.4,.4,1)`,
        display: 'flex',
        flexDirection: 'column',
        mx: 'auto',
        my: 'auto',
        flexShrink: 0,
      }}>
        <Box sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: revealing
            ? `${spin} 1.5s cubic-bezier(.3,.9,.4,1)`
            : `${floatUp} 0.6s ease-out`,
          perspective: 600,
        }}>
          {image ? (
            <Box
              component="img"
              src={image}
              alt={name}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block',
              }}
            />
          ) : (
            <GiftBoxArt color={color} size={120} />
          )}
        </Box>
        {name ? (
          <Box sx={{
            py: 1.25, px: 1.5,
            background: `linear-gradient(180deg, transparent, rgba(0,0,0,0.75))`,
            position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 2,
          }}>
            <Typography sx={{
              color: '#fff', fontWeight: 800, fontSize: { xs: 16, sm: 22 },
              textAlign: 'center', textShadow: '0 2px 8px rgba(0,0,0,0.6)',
              position: 'relative', zIndex: 1,
            }}>
              {name}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Backdrop>
  );
};

/* ── display only (show without a player) ─────────────────────── */

const DisplayCard = ({ screen, revealing }) => {
  const { inline } = useContext(PrizeFrameContext);
  const stage = !inline;
  const color = screen.category_color || '#FFD54F';
  const image = mediaUrl(screen.prize_image_url);
  const value = prizeValueLabel(screen);
  const name = screen.prize_name || 'Prize';

  const copy = (
    <>
      <Typography sx={{
        color: '#fff', fontWeight: 900, mt: stage ? 0 : 2.25, lineHeight: 1.05,
        fontSize: stage ? 'clamp(28px, 4.6vw, 64px)' : { xs: 28, sm: 40 },
      }}>
        {name}
      </Typography>
      {image && value ? (
        <Box sx={{
          display: 'inline-flex', mt: stage ? 1.5 : 1.25, px: stage ? 2 : 1.5, py: stage ? 0.55 : 0.4,
          borderRadius: 999, bgcolor: `${color}22`, border: `1px solid ${color}88`,
        }}>
          <Typography sx={{ color, fontWeight: 800, fontSize: stage ? 'clamp(16px, 2.2vw, 28px)' : { xs: 15, sm: 18 } }}>{value}</Typography>
        </Box>
      ) : null}
    </>
  );

  return (
    <Backdrop strong tint={`${color}55`}>
      <Box sx={{
        position: 'relative',
        ...(stage ? prizeStageFrameSx : {
          width: 'min(560px, 96vw)',
          maxHeight: '92dvh',
        }),
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4,
        bgcolor: '#100e08',
        border: `2px solid ${color}`,
        textAlign: 'center',
        boxShadow: `0 0 80px ${color}33, 0 24px 70px rgba(0,0,0,0.65)`,
        animation: `${popIn} 700ms cubic-bezier(.2,1.4,.4,1)`,
      }}>
        <StageHeader large={stage} color={color} kicker={screen.category_name || 'Prize'} title="On screen" />
        {stage ? (
          <PrizeStageBody color={color} image={image} name={name} value={value} glowing revealing={revealing}>
            {copy}
          </PrizeStageBody>
        ) : (
          <Box sx={{ px: { xs: 2, sm: 4 }, pt: 2.5, pb: 3.5 }}>
            <Box sx={{
              animation: revealing ? `${spin} 1.5s cubic-bezier(.3,.9,.4,1)` : `${floatUp} 0.6s ease-out`,
              perspective: 600,
            }}>
              <PrizeArt image={image} name={name} color={color} size={520} glowing value={value} />
            </Box>
            {copy}
          </Box>
        )}
      </Box>
    </Backdrop>
  );
};

/* ── audience card ────────────────────────────────────────────── */

const AudienceCard = ({ screen }) => {
  const { inline } = useContext(PrizeFrameContext);
  const stage = !inline;
  const awarded = screen.status === 'awarded';
  const color = screen.category_color || '#FFD54F';
  const image = mediaUrl(screen.prize_image_url);
  const value = prizeValueLabel(screen);
  const guestId = (screen.winner_guest_id || '').trim();
  const prizeName = screen.prize_name || 'Prize';

  const lead = (
    <>
      <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 'clamp(26px, 4vw, 52px)', lineHeight: 1.1 }}>
        {screen.winner_name || 'A player'}
      </Typography>
      {guestId ? (
        <Typography sx={{ color, fontWeight: 700, mt: 0.5, fontSize: 'clamp(14px, 1.8vw, 20px)' }}>
          Guest {guestId}
        </Typography>
      ) : null}
    </>
  );
  const copy = (
    <>
      <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 'clamp(18px, 2.4vw, 32px)' }}>{prizeName}</Typography>
      {image && value ? (
        <Typography sx={{ color, mt: 0.4, fontWeight: 800, fontSize: 'clamp(16px, 2vw, 26px)' }}>{value}</Typography>
      ) : null}
      <Typography sx={{ color: '#9a9a9a', mt: 1, fontSize: 'clamp(14px, 1.6vw, 20px)' }}>
        {awarded ? 'Just won this prize' : 'Is up for this prize'}
      </Typography>
    </>
  );

  const card = (
    <Box sx={{
      ...(stage ? prizeStageFrameSx : { width: 'min(440px, 94vw)' }),
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 4,
      bgcolor: '#141414',
      border: `1px solid ${awarded ? color : '#333'}`,
      textAlign: 'center',
      overflow: 'hidden',
      boxShadow: `0 20px 60px rgba(0,0,0,0.55)${awarded ? `, 0 0 36px ${color}22` : ''}`,
    }}>
      <StageHeader large={stage} color={color} kicker="Prize" title={awarded ? 'Awarded' : 'On screen'} />
      {stage ? (
        <PrizeStageBody color={color} image={image} name={prizeName} value={value} lead={lead}>
          {copy}
        </PrizeStageBody>
      ) : (
        <Box sx={{ px: 2.5, pt: 2.25, pb: 2.5 }}>
          <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: 22, sm: 26 }, lineHeight: 1.1 }}>
            {screen.winner_name || 'A player'}
          </Typography>
          {guestId ? (
            <Typography sx={{ color, fontWeight: 700, mt: 0.4, fontSize: 13 }}>
              Guest {guestId}
            </Typography>
          ) : null}
          <Box sx={{ my: 2 }}>
            <PrizeArt image={image} name={prizeName} color={color} size={400} value={value} />
          </Box>
          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 17 }}>{prizeName}</Typography>
          {image && value ? (
            <Typography sx={{ color, mt: 0.35, fontWeight: 800 }}>{value}</Typography>
          ) : null}
          <Typography sx={{ color: '#9a9a9a', mt: 1, fontSize: 13 }}>
            {awarded ? 'Just won this prize' : 'Is up for this prize'}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Backdrop tint={awarded ? `${color}44` : 'rgba(40,40,40,0.45)'}>
      {stage ? card : <Grow in timeout={400}>{card}</Grow>}
    </Backdrop>
  );
};

/* ── wheel card (forced prize wheel, hot seat mechanics) ──────── */

const WheelCard = ({ screen }) => {
  const { inline } = useContext(PrizeFrameContext);
  const stage = !inline;
  const phone = useMediaQuery('(max-width:599.98px)', { noSsr: true });
  const color = screen.category_color || '#CE93D8';
  const playerName = screen.player_name || 'Player';
  const prizeName = screen.prize_name || 'Wheel';
  const bgImage = screen.background_image_url || '';

  const entryFace = sliceSignature((screen.entries || []).filter((e) => !e.is_excluded));
  const entries = useMemo(
    () => (screen.entries || []).filter((e) => !e.is_excluded),
    // Keep the same slice objects when a lock/unlock refresh does not change the art.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entryFace]
  );
  const [spinning, setSpinning] = useState(false);
  const frozenEntriesRef = useRef(entries);
  if (!spinning) frozenEntriesRef.current = entries;
  const wheelSlices = spinning ? frozenEntriesRef.current : entries;
  const [finalAngle, setFinalAngle] = useState(0);
  const [spinDuration, setSpinDuration] = useState(6);
  const [prizeIndex, setPrizeIndex] = useState(0);
  const [animComplete, setAnimComplete] = useState(false);
  const [winnerEntry, setWinnerEntry] = useState(null);
  const [playerCanSpin, setPlayerCanSpin] = useState(!!screen.player_can_spin);
  const [playerSpinUnlocked, setPlayerSpinUnlocked] = useState(!!screen.player_spin_unlocked);
  const [spinBusy, setSpinBusy] = useState(false);
  const [wheelLanded, setWheelLanded] = useState(false);

  const hotSeatDeviceId = screen.hot_seat_device_id || '';
  const hotSeatUserId = screen.hot_seat_user_id;
  const socketRef = useRef(null);
  const entriesRef = useRef(entries);
  const spinningRef = useRef(false);
  const { playSpinStart, playWinnerClap } = usePrizeWheelSpinAudio();
  const playSpinStartRef = useRef(playSpinStart);
  const playWinnerClapRef = useRef(playWinnerClap);
  entriesRef.current = entries;
  spinningRef.current = spinning;
  playSpinStartRef.current = playSpinStart;
  playWinnerClapRef.current = playWinnerClap;

  const isHotSeatPlayer = useMemo(() => {
    const myDevice = readDeviceId();
    const myUser = readUserId();
    if (hotSeatUserId != null && myUser != null && Number(hotSeatUserId) === Number(myUser)) return true;
    if (hotSeatDeviceId && myDevice && String(hotSeatDeviceId) === String(myDevice)) return true;
    return false;
  }, [hotSeatDeviceId, hotSeatUserId]);

  // Sync player_can_spin / player_spin_unlocked from screen updates
  useEffect(() => {
    setPlayerCanSpin(!!screen.player_can_spin);
    setPlayerSpinUnlocked(!!screen.player_spin_unlocked);
  }, [screen.player_can_spin, screen.player_spin_unlocked]);

  useEffect(() => {
    if (spinning) return;
    const fromScreen = entries.find((entry) => entry.is_winner);
    if (!fromScreen) return;
    setWinnerEntry((prev) => prev || fromScreen);
  }, [entries, spinning]);

  // Join spinwheel:live room to receive hot seat events
  useEffect(() => {
    const socket = io(API_BASE, { withCredentials: true });
    socketRef.current = socket;

    socket.on('connect', () => { socket.emit('spinwheel:join-live'); });

    socket.on('spinwheel:hot_seat_spin_start', ({ finalAngle: fa, spinDuration: sd, winnerIdx, winnerId }) => {
      const list = entriesRef.current;
      const landed = entryFromSpin(winnerIdx, winnerId, list);
      setPrizeIndex(prizeIndexFromSpin(winnerIdx, winnerId, list));
      setFinalAngle(fa);
      setSpinDuration(sd || 6);
      setAnimComplete(false);
      setWheelLanded(false);
      setWinnerEntry(landed);
      setSpinning(true);
      playSpinStartRef.current();
    });

    socket.on('spinwheel:hot_seat_spin_complete', ({ winner }) => {
      if (winner) setWinnerEntry(winner);
    });

    socket.on('spinwheel:hot_seat_updated', ({ session }) => {
      if (session) {
        setPlayerCanSpin(!!session.player_can_spin);
        setPlayerSpinUnlocked(!!session.player_spin_unlocked);
      }
    });

    return () => { socket.disconnect(); socketRef.current = null; };
  }, []);

  const handlePlayerSpin = async () => {
    if (!isHotSeatPlayer || !playerCanSpin || !playerSpinUnlocked || spinning || spinBusy) return;
    setSpinBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/spinning-wheel/hot-seat/spin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-device-id': readDeviceId() },
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Spin failed');
      setPlayerSpinUnlocked(false);
      setAnimComplete(false);
      setWheelLanded(false);
      if (!socketRef.current?.connected) {
        const landed = entryFromSpin(data.winnerIdx, data.winner?.id, entries);
        setWinnerEntry(landed);
        setPrizeIndex(prizeIndexFromSpin(data.winnerIdx, data.winner?.id, entries));
        setFinalAngle(data.finalAngle);
        setSpinDuration(data.spinDuration || 6);
        setSpinning(true);
        playSpinStartRef.current();
      }
    } catch { /* socket events will handle it */ }
    finally { setSpinBusy(false); }
  };

  const handleAnimEnd = useCallback(() => {
    setSpinning(false);
    setAnimComplete(true);
    setWheelLanded(false);
  }, []);

  const handleLanded = useCallback(() => setWheelLanded(true), []);

  const handleWinnerReveal = useCallback(() => {
    playWinnerClapRef.current();
  }, []);

  const showSpinButton = isHotSeatPlayer && playerCanSpin && playerSpinUnlocked && !spinning && !spinBusy;
  const landedName = winnerEntry?.display_name || winnerEntry?.text || '';
  const showResult = !spinning && !!landedName;
  const prizeImage = mediaUrl(screen.prize_image_url);
  const [wheelBoxRef, wheelBox] = useElementBox(stage);
  const compactWheelSize = Math.min(340, Math.max(230, (typeof window !== 'undefined' ? window.innerWidth : 390) - 64));
  // Fit the wheel to its own box. Never let it grow into the turn name above it.
  const fittedWheel = wheelBox.w > 0 && wheelBox.h > 0
    ? Math.max(0, Math.floor(Math.min(wheelBox.w, wheelBox.h) - 8))
    : 0;
  // On a phone the wheel matches the card width, so it stays large even though the card is taller than square.
  const phoneWheelSize = Math.max(220, Math.floor((typeof window !== 'undefined' ? window.innerWidth : 390) - 100));
  const wheelSize = !stage ? compactWheelSize : phone ? (fittedWheel > 0 ? fittedWheel : phoneWheelSize) : fittedWheel;
  let status = `Watching ${playerName}`;
  if (spinning && wheelLanded) status = 'Here it comes…';
  else if (spinning) status = 'Spinning…';
  else if (showResult) status = `Landed on ${landedName}`;
  else if (isHotSeatPlayer && showSpinButton) status = 'Your spin is unlocked';
  else if (isHotSeatPlayer && playerCanSpin) status = 'Waiting for the host to unlock your spin';
  else if (isHotSeatPlayer) status = 'The host spins this one';

  return (
    <Backdrop strong tint={`${color}55`}>
      <Box sx={{
        position: 'relative',
        ...(stage ? {
          // Desktop stays a 1:1 card. On a phone the card grows with the wheel so the wheel can fill the width.
          ...prizeStageFrameSx,
          width: phone ? showPopupMaxW : showPopupSquare,
          height: phone ? 'auto' : showPopupSquare,
          maxWidth: showPopupMaxW,
          maxHeight: stageMaxH,
          aspectRatio: phone ? 'auto' : '1 / 1',
          containerType: phone ? 'normal' : 'size',
        } : {
          width: 'min(96vw, 460px)',
          maxWidth: '100%',
          maxHeight: '96vh',
          overflow: 'auto',
        }),
        borderRadius: 4,
        bgcolor: '#0d0b14',
        border: `2px solid ${color}`,
        textAlign: 'center',
        boxShadow: `0 0 80px ${color}33, 0 24px 70px rgba(0,0,0,0.7)`,
        animation: `${popIn} 700ms cubic-bezier(.2,1.4,.4,1)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        boxSizing: 'border-box',
      }}>
        <StageHeader compact={stage} large={stage} color={color} kicker="Prize wheel" title={prizeName} />

        <Box sx={{
          alignSelf: 'center',
          mt: stage ? 0.75 : 1.75,
          px: 1.5,
          py: stage ? 0.35 : 0.45,
          borderRadius: 999,
          bgcolor: isHotSeatPlayer ? `${color}24` : 'rgba(255,255,255,0.06)',
          border: `1px solid ${isHotSeatPlayer ? color : 'rgba(255,255,255,0.12)'}`,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          maxWidth: '90%',
          flexShrink: 0,
          mb: stage ? 0.5 : 1.5,
        }}>
          {prizeImage ? (
            <Box component="img" src={prizeImage} alt="" sx={{ width: stage ? 28 : 22, height: stage ? 28 : 22, borderRadius: '50%', objectFit: 'cover' }} />
          ) : null}
          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: stage ? 'clamp(14px, 1.8vw, 22px)' : 13, lineHeight: 1.2 }}>
            {isHotSeatPlayer ? 'Your turn' : `For ${playerName}`}
          </Typography>
        </Box>

        <Box
          ref={wheelBoxRef}
          sx={{
            px: phone ? 1 : 2,
            pt: stage ? 0.5 : 1,
            pb: stage ? 0.5 : 1,
            width: '100%',
            flex: stage && !phone ? '1 1 0' : '0 0 auto',
            aspectRatio: stage && phone ? '1 / 1' : undefined,
            maxHeight: stage && phone ? 'calc(100dvh - 210px)' : undefined,
            minHeight: stage && !phone ? 0 : undefined,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {wheelSlices.length >= 2 && wheelSize > 0 ? (
            <StableLiveWheel
              entries={wheelSlices}
              spinning={spinning}
              finalAngle={finalAngle}
              spinDuration={spinDuration}
              winnerEntry={winnerEntry}
              animationComplete={animComplete}
              onAnimationEnd={handleAnimEnd}
              size={wheelSize}
              centerLogoSrc="/mind_your_music.png"
              hotSeatPlayerName={playerName}
              backgroundImageUrl={bgImage}
              variant="default"
              prizeIndex={prizeIndex}
              suspenseSpin
              revealDelayMs={2000}
              onLanded={handleLanded}
              onWinnerReveal={handleWinnerReveal}
              showResultCaption={false}
            />
          ) : wheelSlices.length < 2 ? (
            <Typography sx={{ color: '#999', py: 4 }}>Wheel not available</Typography>
          ) : null}
        </Box>

        <Box sx={{
          px: 2.5,
          pt: stage ? 0.75 : 1.5,
          pb: stage ? 1.25 : 1.75,
          width: '100%',
          // Desktop keeps a fixed band so the wheel does not jump. On a phone the band hugs the text so the wheel can stay large.
          height: stage && !phone ? 'clamp(104px, 24cqmin, 148px)' : 'auto',
          flexShrink: 0,
          boxSizing: 'border-box',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: stage ? 0.5 : 0.75,
        }}>
          {showResult ? (
            <Typography sx={{
              color: '#FFD700',
              fontWeight: 700,
              fontSize: stage ? 'clamp(15px, 1.7vw, 22px)' : '0.95rem',
              lineHeight: 1.2,
            }}>
              {`Prize${landedName ? `: ${landedName}` : ''}`}
            </Typography>
          ) : null}
          <Typography sx={{
            color: showResult ? color : 'rgba(255,255,255,0.72)',
            fontWeight: 700,
            fontSize: stage ? 'clamp(16px, 2.1vw, 26px)' : 14,
            lineHeight: 1.25,
            animation: !showResult && !spinning && isHotSeatPlayer ? `${pulse} 2s ease-in-out infinite` : 'none',
          }}>
            {status}
          </Typography>
          {showSpinButton ? (
            <Button
              variant="contained"
              fullWidth
              disabled={spinBusy}
              onClick={handlePlayerSpin}
              sx={{
                mt: 0.5,
                maxWidth: stage ? 420 : 280,
                py: stage ? 1.15 : 1,
                fontSize: stage ? 'clamp(18px, 2vw, 24px)' : '1.05rem',
                fontWeight: 900,
                letterSpacing: 0.6,
                borderRadius: 999,
                background: `linear-gradient(135deg, ${color}, #7b1fa2)`,
                color: '#fff',
                boxShadow: `0 8px 24px ${color}55`,
                '&:hover': { background: `linear-gradient(135deg, #7b1fa2, ${color})` },
              }}
            >
              {spinBusy ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Spin'}
            </Button>
          ) : null}
        </Box>
      </Box>
    </Backdrop>
  );
};

export default PrizeRevealOverlay;
